import { Clear, InfoOutlined, Search } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  List,
  ListItem,
  Stack,
  Switch,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  IUnallocatedTasksResProperties,
  useGetUnallocatedTasks,
} from 'src/apis/resourcePlannerAPI';
import grays from 'src/assets/styles/variables/colors/global/grays.module.scss';
import { useGetLocale } from 'src/components/global/LocaleProvider';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from 'src/components/mui-components';
import { HighlightMatchingText } from 'src/components/utils/HighlightMatchingText';
import { ResponseHandler } from 'src/components/utils/ResponseHandler';
import { getDateStringFromSiteLocale } from 'src/utils/date';
import { useDebounce } from 'use-debounce';
import { useKeyboardControl } from '../../components/AssignFlow/hooks';
import { AssignDialog } from '../../components/AssignFlow/components/AssignDialog';
import styles from './SearchForTaskDialog.module.scss';
import { SkeletonItem } from '../components';

export const SearchForTaskDialog = () => {
  const { t } = useTranslation('assignFlow');
  const siteLocale = useGetLocale();
  const navigate = useNavigate();
  const { id: resourceSourceReferenceId, projectId } = useParams<{
    id: string;
    projectId: string;
  }>() as {
    id: string;
    projectId: string;
  };
  const [searchParams] = useSearchParams();
  const name = searchParams.get('name') ?? '';
  const projectName = searchParams.get('projectName');

  const [taskQuery, setTaskQuery] = useState('');
  const [debouncedTaskQuery] = useDebounce(taskQuery, 500);
  const [showParentTasks, setShowParentTasks] = useState(false);

  const {
    data: taskListData,
    observerRef: taskListObserverRef,
    hasNextPage: taskListHasNextPage,
    isLoading: taskListIsLoading,
    isError: taskListIsError,
    isFetchingNextPage: taskListIsFetchingNextPage,
  } = useGetUnallocatedTasks({
    isIncludeParentTask: showParentTasks,
    projectId,
    searchText: debouncedTaskQuery,
    userId: resourceSourceReferenceId,
  });

  const {
    dialogOnKeyDown,
    initialFocusElement,
    searchContainerResults,
    selectItemIndex,
    setSelectItemIndex,
    textFieldOnKeyDown,
  } = useKeyboardControl({
    hasLazyLoad: true,
  });

  useEffect(() => {
    initialFocusElement.current?.select();
  }, [initialFocusElement]);

  const cleanUp = () => {
    navigate(-2);
    setTaskQuery('');
    setShowParentTasks(false);
  };

  const dialogOnClose = () => {
    cleanUp();
  };

  const getParentTaskName = ({ taskFullName, taskName }: IUnallocatedTasksResProperties) =>
    taskFullName.replace(taskName, '');

  return (
    <Dialog
      data-automation-id="SearchForTaskDialog"
      fullWidth
      maxWidth="md"
      onClose={dialogOnClose}
      onKeyDown={dialogOnKeyDown}
      open
      PaperProps={{ sx: { overflowY: 'unset' } }}
      slotProps={{
        backdrop: {
          transitionDuration: {
            enter: 0,
          },
        },
      }}
    >
      <DialogTitle component={Box} data-automation-id="SearchForTaskDialogTitle">
        <Stack gap={2}>
          <Typography lineHeight={1.6} variant="h2">
            <Trans
              i18nKey="AssignEmployeeToTaskHeader"
              key="AssignEmployeeToTaskHeader"
              defaults={t('AssignEmployeeToTaskHeader')}
              values={{ employee: name, projectName }}
              components={[<strong key={0}>{name}</strong>, <strong key={1}>{projectName}</strong>]}
            />
          </Typography>
          <Stack alignItems="center" direction="row" gap={2}>
            <TextField
              ariaLabel={t('SearchForTasksInputLabel')}
              data-automation-id="SuperSearchForTasks"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {taskQuery ? (
                      <IconButton
                        data-automation-id="SearchForTasksInputClearButton"
                        onClick={() => setTaskQuery('')}
                        size="small"
                        title={t('ClearSearchButtonText')}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    ) : null}
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                inputProps: { autoFocus: true, autoComplete: 'off' },
              }}
              inputRef={initialFocusElement}
              label={t('SearchForTasksInputLabel')}
              onChange={(e) => setTaskQuery(e.target.value)}
              onKeyDown={textFieldOnKeyDown}
              value={taskQuery}
            />
            <Tooltip
              tabIndex={0}
              title={
                <Stack>
                  <Typography component="h3" fontWeight={700} lineHeight={1.5} variant="h4">
                    {t('SearchForTasksTooltipHeading')}
                  </Typography>
                  <Typography variant="small" sx={{ whiteSpace: 'pre-line' }}>
                    {t('SearchForTasksTooltip')}
                  </Typography>
                </Stack>
              }
            >
              <InfoOutlined />
            </Tooltip>
          </Stack>
          <FormControlLabel
            control={
              <Switch
                onChange={(_, checked) => setShowParentTasks(checked)}
                value={showParentTasks}
              />
            }
            label={t('ShowWBStructureLabel')}
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers onMouseEnter={() => setSelectItemIndex(-1)} sx={{ pt: 2, zIndex: 0 }}>
        <ResponseHandler
          EmptyComponent={<Typography textAlign="center">{t('NoResults')}</Typography>}
          isError={taskListIsError}
          isEmpty={!taskListData.length}
          isLoading={taskListIsLoading}
          LoadingComponent={
            <List className={styles.list} sx={{ minHeight: 460 }}>
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
            </List>
          }
        >
          <List className={styles.list} ref={searchContainerResults}>
            {taskListData.map((task, i) => {
              if (task) {
                const { properties } = task;

                return (
                  <ListItem
                    data-automation-id={`TaskItem-${properties.taskID}`}
                    disablePadding
                    key={properties.taskID}
                    sx={{ position: 'relative' }}
                  >
                    <AssignDialog
                      dates={[new Date(properties.taskStartDate), new Date(properties.taskEndDate)]}
                      from={name}
                      projectId={projectId}
                      selected={selectItemIndex === i}
                      taskId={properties.taskID}
                      to={properties.taskName}
                      userId={resourceSourceReferenceId}
                    >
                      <Box className={styles.taskItem}>
                        <HighlightMatchingText matchName={taskQuery} name={properties.wBSNo} />
                        <Stack gap={0.25}>
                          <Box>
                            <Typography color={grays.colorGray55} component="span">
                              {showParentTasks ? (
                                <HighlightMatchingText
                                  matchName={taskQuery}
                                  name={getParentTaskName(properties)}
                                />
                              ) : null}
                            </Typography>
                            <HighlightMatchingText
                              matchName={taskQuery}
                              name={properties.taskName}
                            />
                            {properties.taskNo ? (
                              <HighlightMatchingText
                                matchName={taskQuery}
                                name={` (${properties.taskNo})`}
                              />
                            ) : null}
                          </Box>
                          <Stack direction="row" flexWrap="wrap">
                            <Typography className={styles.separatedLabels} variant="small">
                              <span>
                                {t('TaskSchedule', {
                                  startDate: getDateStringFromSiteLocale(
                                    new Date(properties.taskStartDate),
                                    siteLocale,
                                  ),
                                  endDate: getDateStringFromSiteLocale(
                                    new Date(properties.taskEndDate),
                                    siteLocale,
                                  ),
                                })}
                              </span>
                              <span>
                                {t('TaskBudget', { budget: properties.taskBudgetHour ?? 0 })}
                              </span>
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </AssignDialog>
                  </ListItem>
                );
              }

              return null;
            })}

            {taskListHasNextPage && (
              <ListItem disablePadding ref={taskListObserverRef}>
                {taskListIsFetchingNextPage && <SkeletonItem />}
              </ListItem>
            )}
          </List>
        </ResponseHandler>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" flex={1} justifyContent="space-between">
          <Button data-automation-id="BackButton" onClick={() => navigate(-1)}>
            {t('BackButtonText')}
          </Button>
          <Button data-automation-id="CancelButton" onClick={cleanUp} variant="outlined">
            {t('CancelButtonText')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
